import { useState } from 'react'
import nexusApi from '../../services/nexusApi'
import {
  Play, CheckCircle, Circle, ChevronRight, Users, DollarSign,
  Star, Target, MessageSquare, Loader2, Building2, Zap
} from 'lucide-react'

const NX = {
  bg: '#0D1117', surface: '#161B22', surface2: '#1C2330', border: '#2A3441',
  text: '#E6EDF3', textDim: '#8B949E', accent: '#0066FF',
  green: '#3DB54A', amber: '#F5921E', red: '#EF4444',
}

const TALKING_POINTS = [
  {
    section: 'Opening',
    points: [
      'HSEQ Intelligence is the only WHS platform built specifically for Australian SMEs under the model WHS Act.',
      'We handle every compliance obligation — incident notification (48h/7d PCBU duties), permit workflows, and Safe Work Australia benchmarking.',
      'Our customers reduce incident admin time by 94% and achieve full digital audit trails in their first week.',
    ]
  },
  {
    section: 'Incident Management',
    points: [
      'Log an incident in under 4 minutes on mobile — faster than a paper form.',
      'AI severity scoring alerts the PCBU automatically when the threshold triggers a regulatory notification.',
      'Every incident automatically spawns corrective actions and assigns owners — nothing falls through the cracks.',
    ]
  },
  {
    section: 'Permits to Work',
    points: [
      'Digital Permits replace paper and email chains. Approvals happen in the platform, with time-expiry and automatic reminders.',
      'Hot work, confined space, and electrical isolation permits all follow the same auditable workflow.',
      'Workers cannot start work until the permit is approved — the app enforces this at the point of action.',
    ]
  },
  {
    section: 'Audits & Compliance',
    points: [
      'Build audit templates from 40+ industry-specific question banks, or import your existing templates.',
      'Conduct audits on mobile in offline mode — findings sync automatically when back online.',
      'Auto-generate signed PDF reports with organisation branding, ready for regulators or clients.',
    ]
  },
  {
    section: 'ROI & Pricing',
    points: [
      'Standard plan at $299/mo includes unlimited users, unlimited incidents, and all core features.',
      'Professional at $599/mo adds analytics, API access, and priority support.',
      'Enterprise at $1,499/mo adds custom integrations, dedicated CSM, and SLA guarantees.',
      'Most customers see ROI in the first month from admin time alone.',
    ]
  },
  {
    section: 'Objection Handling',
    points: [
      '"We already have spreadsheets" → Ask: How do you prove due diligence if the regulator calls? A spreadsheet can\'t audit itself.',
      '"Too expensive" → One WHS investigation costs $40k+ on average. This platform pays for itself if it prevents one incident.',
      '"We tried software before" → We\'re mobile-first for field workers, not built for desk users. 80% of incidents are reported on mobile.',
      '"We need to check with IT" → HSEQ Intelligence is SaaS — no infrastructure, no deployment. IT requirement is zero.',
    ]
  },
]

const SCENARIOS = [
  { id: 1, name: 'Log a Near Miss', path: '/incidents/new', description: 'Show how field workers capture incidents on mobile in <4 minutes', duration: '5 min', difficulty: 'Easy' },
  { id: 2, name: 'Run a Safety Audit', path: '/audits', description: 'Demonstrate template-based audits, findings, and PDF reports', duration: '8 min', difficulty: 'Medium' },
  { id: 3, name: 'Approve a Hot Work Permit', path: '/permits', description: 'Show multi-step permit approval with automatic expiry', duration: '4 min', difficulty: 'Easy' },
  { id: 4, name: 'Dashboard & Analytics', path: '/dashboard', description: 'Walk through live KPIs, trends, and export capabilities', duration: '6 min', difficulty: 'Easy' },
  { id: 5, name: 'Role-Based Access', path: '/settings/roles', description: 'Show how different users see different data based on role', duration: '5 min', difficulty: 'Medium' },
  { id: 6, name: 'Lone Worker Monitoring', path: '/lone-worker/monitor', description: 'Demonstrate real-time lone worker monitoring and SOS', duration: '4 min', difficulty: 'Easy' },
  { id: 7, name: 'External Contractor Portal', path: '/settings/external-access', description: 'Show how contractors get scoped access without full accounts', duration: '5 min', difficulty: 'Medium' },
]

export default function NexusSalesDemo() {
  const [creating, setCreating] = useState(false)
  const [demoResult, setDemoResult] = useState<any>(null)
  const [selectedIndustry, setSelectedIndustry] = useState('field_services')
  const [expandedSection, setExpandedSection] = useState<string | null>('Opening')
  const [completedScenarios, setCompletedScenarios] = useState<Set<number>>(new Set())

  async function createDemo() {
    setCreating(true)
    try {
      const r = await nexusApi.post('/demo/create', { industry: selectedIndustry })
      setDemoResult(r.data)
    } catch (e: any) {
      alert(e.response?.data?.error || 'Failed to create demo')
    } finally { setCreating(false) }
  }

  return (
    <div style={{ padding: '2rem', maxWidth: 1100 }}>
      <div style={{ marginBottom: '1.75rem' }}>
        <h1 style={{ color: NX.text, fontSize: '1.375rem', fontWeight: 700, margin: 0 }}>Sales Demo Mode</h1>
        <p style={{ color: NX.textDim, fontSize: '0.875rem', margin: '0.25rem 0 0' }}>Create demo environments and access sales playbooks</p>
      </div>

      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '1.5rem', marginBottom: '1.5rem' }}>
        {/* Create Demo Environment */}
        <div style={{ background: NX.surface, border: `1px solid ${NX.border}`, borderRadius: 12, padding: '1.5rem' }}>
          <h2 style={{ color: NX.text, fontSize: '1rem', fontWeight: 600, margin: '0 0 1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Play size={16} color={NX.accent} /> Create Demo Environment
          </h2>
          <div style={{ marginBottom: '1rem' }}>
            <label style={{ display: 'block', color: NX.textDim, fontSize: '0.75rem', marginBottom: '0.5rem' }}>Industry</label>
            <select
              value={selectedIndustry}
              onChange={e => setSelectedIndustry(e.target.value)}
              style={{ width: '100%', background: NX.surface2, border: `1px solid ${NX.border}`, borderRadius: 8, color: NX.text, padding: '0.625rem 0.875rem', fontSize: '0.875rem' }}
            >
              <option value="field_services">Field Services — Apex Field Services</option>
              <option value="construction">Construction — Redstone Construction Group</option>
              <option value="mining">Mining — Cornerstone Mining Operations</option>
            </select>
          </div>
          <button
            onClick={createDemo}
            disabled={creating}
            style={{
              width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '0.5rem',
              background: NX.accent, border: 'none', borderRadius: 8, color: '#fff',
              padding: '0.75rem', cursor: 'pointer', fontSize: '0.875rem', fontWeight: 600,
              opacity: creating ? 0.7 : 1,
            }}
          >
            {creating ? <Loader2 size={16} /> : <Play size={16} />}
            {creating ? 'Creating demo…' : 'Generate Demo Environment'}
          </button>

          {demoResult && (
            <div style={{ marginTop: '1.25rem', background: NX.surface2, borderRadius: 10, padding: '1rem', border: `1px solid ${NX.green}40` }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 6, color: NX.green, fontWeight: 600, fontSize: '0.875rem', marginBottom: '0.75rem' }}>
                <CheckCircle size={14} /> Demo environment ready
              </div>
              <div style={{ fontSize: '0.8125rem', color: NX.textDim, marginBottom: '0.5rem' }}>
                <strong style={{ color: NX.text }}>URL:</strong> {window.location.origin}/demo
              </div>
              <div style={{ fontSize: '0.8125rem', color: NX.textDim, marginBottom: '0.5rem' }}>
                <strong style={{ color: NX.text }}>Admin:</strong> {demoResult.user?.email}
              </div>
              <div style={{ fontSize: '0.8125rem', color: NX.textDim, marginBottom: '0.75rem' }}>
                <strong style={{ color: NX.text }}>Password:</strong> Demo2026! (all users)
              </div>
              <div style={{ marginTop: '0.75rem' }}>
                <div style={{ color: NX.textDim, fontSize: '0.75rem', fontWeight: 600, marginBottom: '0.5rem' }}>ALL DEMO USERS</div>
                {(demoResult.users || []).map((u: any) => (
                  <div key={u.email} style={{ display: 'flex', justifyContent: 'space-between', padding: '3px 0', borderBottom: `1px solid ${NX.border}` }}>
                    <span style={{ color: NX.text, fontSize: '0.75rem' }}>{u.name}</span>
                    <div style={{ display: 'flex', gap: 8 }}>
                      <span style={{ color: NX.textDim, fontSize: '0.75rem', textTransform: 'capitalize' }}>{u.role}</span>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>

        {/* Demo Scenarios */}
        <div style={{ background: NX.surface, border: `1px solid ${NX.border}`, borderRadius: 12, padding: '1.5rem' }}>
          <h2 style={{ color: NX.text, fontSize: '1rem', fontWeight: 600, margin: '0 0 1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
            <Target size={16} color={NX.amber} /> Demo Scenarios
          </h2>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
            {SCENARIOS.map(s => {
              const done = completedScenarios.has(s.id)
              return (
                <div key={s.id} style={{ background: NX.surface2, borderRadius: 8, padding: '0.75rem', display: 'flex', gap: '0.75rem' }}>
                  <button onClick={() => setCompletedScenarios(prev => {
                    const n = new Set(prev); done ? n.delete(s.id) : n.add(s.id); return n
                  })} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, marginTop: 2, flexShrink: 0 }}>
                    {done ? <CheckCircle size={16} color={NX.green} /> : <Circle size={16} color={NX.textDim} />}
                  </button>
                  <div style={{ flex: 1 }}>
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
                      <span style={{ color: NX.text, fontSize: '0.8125rem', fontWeight: 500 }}>{s.id}. {s.name}</span>
                      <div style={{ display: 'flex', gap: 4, flexShrink: 0, marginLeft: 8 }}>
                        <span style={{ background: `${NX.accent}20`, color: NX.accent, fontSize: '0.6875rem', padding: '1px 6px', borderRadius: 4 }}>{s.duration}</span>
                        <span style={{ background: s.difficulty === 'Easy' ? `${NX.green}20` : `${NX.amber}20`, color: s.difficulty === 'Easy' ? NX.green : NX.amber, fontSize: '0.6875rem', padding: '1px 6px', borderRadius: 4 }}>{s.difficulty}</span>
                      </div>
                    </div>
                    <div style={{ color: NX.textDim, fontSize: '0.75rem', marginTop: 2 }}>{s.description}</div>
                  </div>
                </div>
              )
            })}
          </div>
          <div style={{ marginTop: '1rem', color: NX.textDim, fontSize: '0.75rem', textAlign: 'right' }}>
            {completedScenarios.size}/{SCENARIOS.length} scenarios completed
          </div>
        </div>
      </div>

      {/* Talking Points */}
      <div style={{ background: NX.surface, border: `1px solid ${NX.border}`, borderRadius: 12, padding: '1.5rem' }}>
        <h2 style={{ color: NX.text, fontSize: '1rem', fontWeight: 600, margin: '0 0 1.25rem', display: 'flex', alignItems: 'center', gap: '0.5rem' }}>
          <MessageSquare size={16} color={NX.green} /> Sales Playbook
        </h2>
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.5rem' }}>
          {TALKING_POINTS.map(section => {
            const open = expandedSection === section.section
            return (
              <div key={section.section} style={{ background: NX.surface2, borderRadius: 10, overflow: 'hidden' }}>
                <button
                  onClick={() => setExpandedSection(open ? null : section.section)}
                  style={{ width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', padding: '0.875rem 1rem', background: 'none', border: 'none', cursor: 'pointer', color: NX.text }}
                >
                  <span style={{ fontWeight: 600, fontSize: '0.875rem' }}>{section.section}</span>
                  <ChevronRight size={16} color={NX.textDim} style={{ transform: open ? 'rotate(90deg)' : 'none', transition: 'transform 0.2s' }} />
                </button>
                {open && (
                  <div style={{ padding: '0 1rem 1rem', display: 'flex', flexDirection: 'column', gap: '0.625rem' }}>
                    {section.points.map((p, i) => (
                      <div key={i} style={{ display: 'flex', gap: '0.75rem', padding: '0.625rem 0.75rem', background: NX.bg, borderRadius: 8, borderLeft: `3px solid ${NX.accent}` }}>
                        <Star size={12} color={NX.accent} style={{ flexShrink: 0, marginTop: 2 }} />
                        <span style={{ color: NX.textDim, fontSize: '0.8125rem', lineHeight: 1.6 }}>{p}</span>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            )
          })}
        </div>
      </div>
    </div>
  )
}
